<script setup lang="ts">
  import { computed, onMounted, ref, watch } from 'vue'
  import { Phone, ArrowUpRight } from 'lucide-vue-next'
  import { TableCell, TableRow } from '@/components/ui/table'
  import { Button } from '@/components/ui/button'
  import { Badge } from '@/components/ui/badge'
  import PaginatedTableView from '@/components/admin/PaginatedTable.vue'
  import type { PaginatedResponse, FetchDataOptions } from '@/components/admin/PaginatedTable.vue'
  import {
    CallsService,
    type TCall,
    type CustomerCallResult,
    OrganisationsService,
    type CustomerOrganisation,
    ClassificationEnum
  } from '@/types/generated'
  import { useRouter } from 'vue-router'
  import { useTranslation } from '@/composables/useTranslation'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'

  const router = useRouter()

  const isVoiceStandAlone = ref(false)
  const withoutMessage = ref(true)
  const selectedLawField = ref('')
  const tableRef = ref()
  const org = ref<CustomerOrganisation>()

  const lawFieldOptions = computed(() => {
    console.log('Organisation Lawfields', org.value?.law_fields)
    if (!org.value?.law_fields) return []
    return (org.value.law_fields as Array<string>).map((value) => ({
      value,
      title: value
    }))
  })

  watch([withoutMessage, selectedLawField], () => {
    if (tableRef.value) {
      tableRef.value.refreshData()
    }
  })

  const fetchCalls = async (options?: FetchDataOptions): Promise<PaginatedResponse<TCall>> => {
    try {
      const params = {
        limit: options?.limit,
        offset: options?.offset,
        search: options?.search,
        resultDataLawField: selectedLawField.value,
        hasResult: true,
        excludeNotCompleted: !withoutMessage.value
      }

      if (withoutMessage.value) {
        params.resultClassification = ClassificationEnum.NOT_COMPLETED
      }

      const response = await CallsService.callsList({
        ...params
      })

      return response as unknown as PaginatedResponse<TCall>
    } catch (error) {
      console.error('Error fetching calls:', error)
      throw error
    }
  }

  const fetchCurrentOrg = async () => {
    try {
      org.value = (await OrganisationsService.organisationsRetrieve({
        uuid: 'current'
      })) as CustomerOrganisation

      isVoiceStandAlone.value = org.value.enable_cases || false
    } catch (error) {
      console.error('Error fetching orgs:', error)
    }
  }

  onMounted(() => {
    fetchCurrentOrg()
  })

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  const handleJumpToCase = (call: TCall) => {
    if (call.case_uuid) {
      window.location.href = `/cases/${call.case_uuid}`
    }
  }

  const { t } = useTranslation({
    en: {
      TimeStamp: 'Time Stamp',
      Status: 'Status',
      Phone: 'Phone',
      FieldTopic: 'Field/Topic',
      Duration: 'Duration',
      Recording: 'Recording',
      Action: 'Action',
      Inquiry: 'Show inquiry',
      ViewDetails: 'Show details',
      Unknown: 'Unknown',
      pageDescription: 'Manage incoming calls and inquiries',
      NotCompleted: 'Without Message',
      NotCompletedButton: 'Show calls without message',
      All: 'All law fields',
      ExistingClient: 'Existing Client',
      NewClient: 'New Client',
      GeneralEnquiry: 'General Enquiry',
      Question: 'Question',
      LawFirmPartner: 'Law Firm Partner'
    },
    de: {
      TimeStamp: 'Eingegangen',
      Status: 'Typ',
      Phone: 'Telefonnummer',
      FieldTopic: 'Rechtsgebiet/Thema',
      Duration: 'Dauer',
      Recording: 'Aufnahme',
      Action: 'Aktion',
      Inquiry: 'Zur Anfrage',
      ViewDetails: 'Zum Anruf',
      Unknown: 'Unbekannt',
      pageDescription: 'Verwalten Sie eingehende Anrufe und Anfragen',
      NotCompleted: 'Ohne Nachricht',
      NotCompletedButton: 'Anrufe ohne Nachricht anzeigen',
      All: 'Alle Rechtsgebiete',
      ExistingClient: 'Bestandsmandant',
      NewClient: 'Neumandant',
      GeneralEnquiry: 'Allgemeine Anfrage',
      Question: 'Frage',
      LawFirmPartner: 'Geschäftspartner'
    }
  })

  const handleViewDetails = (call: TCall) => {
    router.push(`/app/calls/${call.uuid}`)
  }

  const getStatusColor = (result: CustomerCallResult) => {
    if (!result) return 'bg-gray-50 text-gray-700 ring-gray-600/20'

    switch (result.classification) {
      case ClassificationEnum.EXISTING_CLIENT_CALL_BACK:
        return 'bg-purple-50 text-purple-700 ring-purple-600/20'

      case ClassificationEnum.NEW_CLIENT_CALL:
        return 'bg-emerald-50 text-emerald-700 ring-emerald-600/20'

      case ClassificationEnum.GENERAL_ENQUIRY:
        return 'bg-sky-50 text-sky-700 ring-sky-600/20'

      case ClassificationEnum.QUESTION:
        return 'bg-amber-50 text-amber-700 ring-amber-600/20'

      case ClassificationEnum.NOT_COMPLETED:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'

      case ClassificationEnum.LAWFIRM_PARTNER:
        return 'bg-indigo-50 text-indigo-700 ring-indigo-600/20'

      default:
        return 'bg-gray-50 text-gray-700 ring-gray-600/20'
    }
  }

  const classificationLabels = {
    EXISTING_CLIENT_CALL_BACK: `${t('ExistingClient')}`,
    NEW_CLIENT_CALL: `${t('NewClient')}`,
    GENERAL_ENQUIRY: `${t('GeneralEnquiry')}`,
    QUESTION: `${t('Question')}`,
    NOT_COMPLETED: `${t('NotCompleted')}`,
    LAWFIRM_PARTNER: `${t('LawFirmPartner')}`
  }

  type ClassificationType = keyof typeof classificationLabels

  const getClassificationLabel = (classification: ClassificationType | undefined) => {
    if (!classification) return t('Unknown')
    const resp = classificationLabels[classification]
    return resp
  }
</script>

<template>
  <SpaCompatComponent>
    <div class="min-h-screen pb-20 pt-5">
      <div class="mx-auto space-y-6">
        <!-- Header -->
        <div>
          <h1 class="text-2xl font-semibold text-gray-900">Anrufe</h1>
          <p class="mt-1 text-sm text-gray-500">{{ t('pageDescription') }}</p>
        </div>

        <!-- Table -->
        <div class="rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-900/5">
          <PaginatedTableView
            ref="tableRef"
            :fetchData="fetchCalls"
            searchable-fields="Name"
          >
            <template #actions>
              <div class="flex items-center gap-4">
                <Button
                  class="flex w-[1/2] items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 pe-9 text-start
                    text-sm hover:bg-gray-100 focus:border-transparent focus:outline-none focus:ring-2
                    focus:ring-purple-500 disabled:opacity-50"
                  @click="
                    () => {
                      withoutMessage = !withoutMessage
                    }
                  "
                >
                  <div class="flex items-center gap-2">
                    <input
                      v-model="withoutMessage"
                      type="checkbox"
                      class="h-5 w-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <label class="cursor-pointer select-none text-sm font-medium text-gray-700">
                      {{ t('NotCompletedButton') }}
                    </label>
                  </div>
                </Button>

                <SelectField
                  v-model="selectedLawField"
                  :options="lawFieldOptions"
                  name="lawField"
                  :placeholder="t('selectField')"
                  :allow-unselect="true"
                  :unselected-option-text="t('All')"
                />
              </div>
            </template>
            <template #table-head>
              <TableCell class="w-[140px] py-4">{{ t('TimeStamp') }}</TableCell>
              <TableCell class="w-[140px] py-4">{{ t('Status') }}</TableCell>
              <TableCell class="w-[320px] py-4">Name</TableCell>
              <TableCell class="w-[160px] py-4">{{ t('Phone') }}</TableCell>
              <TableCell class="w-[120px] py-4">{{ t('FieldTopic') }}</TableCell>
              <TableCell class="w-[100px] py-4">{{ t('Duration') }}</TableCell>
              <TableCell class="w-[100px] py-4"></TableCell>
            </template>

            <template #table-row="{ items }">
              <TableRow
                v-for="call in items"
                :key="call.uuid"
                class="group border-b border-gray-100 hover:bg-gray-50/50"
              >
                <TableCell class="whitespace-nowrap py-4 text-sm text-gray-600">
                  {{ formatDate(call.call_start_time) }}
                </TableCell>
                <TableCell>
                  <Badge :class="`${getStatusColor(call.result)} whitespace-nowrap`">
                    {{ getClassificationLabel(call.result?.classification) || t('Unknown') }}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div class="font-medium text-gray-900">
                    {{
                      `${call.result?.data?.client?.first_name ?? ''} ${call.result?.data?.client?.last_name ?? ''}`.trim() ||
                      '_'
                    }}
                  </div>
                </TableCell>
                <TableCell class="whitespace-nowrap">
                  <div class="flex items-center gap-2 text-sm text-gray-600">
                    <Phone class="h-3 w-3 text-gray-400" />
                    {{ call.result?.data?.client?.phone || '-' }}
                  </div>
                </TableCell>
                <TableCell class="text-xs text-gray-600">
                  {{ call.result?.data?.law?.field }}
                  <br />
                  {{ call.result?.data?.law?.topic }}
                </TableCell>
                <TableCell class="text-sm text-gray-600">
                  {{ formatDuration(call.duration) }}
                </TableCell>
                <TableCell class="pr-4">
                  <div class="flex items-center justify-end gap-2">
                    <Button
                      v-if="call.case_uuid && isVoiceStandAlone"
                      size="sm"
                      class="flex gap-2 border bg-white text-purple-600 hover:bg-purple-50 hover:text-purple-700"
                      @click="handleJumpToCase(call)"
                    >
                      <ArrowUpRight class="h-3 w-3" />
                      {{ t('Inquiry') }}
                    </Button>
                    <Button
                      size="sm"
                      class="flex gap-2 border bg-white text-black hover:bg-purple-50 hover:text-purple-700"
                      @click="handleViewDetails(call)"
                    >
                      {{ t('ViewDetails') }}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            </template>
          </PaginatedTableView>
        </div>
      </div>
    </div>
  </SpaCompatComponent>
</template>
